/**
 * ModoForjaButton - Header button that expands into full control widget when active
 * Premium styling with flame animation, glow effects, and inline controls
 * Replaces the floating ModoForjaWidget for a cleaner UX
 */

import React from 'react';
import { Flame, Play, Pause, Music, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useModoForja } from '@/contexts/ModoForjaContext';

export default function ModoForjaButton() {
  const { 
    isRunning, 
    isMinimized,
    sessionType, 
    timeLeft,
    currentCycle,
    totalCycles,
    isRadioPlaying,
    openModoForja, 
    expandModoForja,
    toggleTimer,
    toggleRadio,
  } = useModoForja();

  // Check if we should show expanded controls (session active and overlay closed)
  const isSessionActive = isRunning || (isMinimized && timeLeft !== undefined && timeLeft > 0);
  const showExpandedControls = isMinimized && isSessionActive;

  const handleMainClick = () => {
    if (isMinimized) {
      expandModoForja();
    } else {
      openModoForja();
    }
  };

  // Format time for display
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Inactive state - simple button
  if (!showExpandedControls) {
    return (
      <Button
        variant="ghost"
        onClick={openModoForja}
        className={cn(
          "relative gap-2 h-9 px-3 rounded-lg transition-all duration-200",
          "border border-transparent hover:border-border/50",
          "hover:bg-secondary/60",
          "hover:shadow-[0_0_20px_-8px_hsl(32_95%_55%/0.3)]"
        )}
      >
        <Flame className="w-4 h-4" />
        <span className="text-xs font-medium hidden sm:inline">Modo Forja</span>
      </Button>
    );
  }

  // Active state - expanded with all controls
  const isFocus = sessionType === 'focus';

  return (
    <div
      className={cn(
        "flex items-center gap-1 h-9 px-1.5 sm:px-2 rounded-lg transition-all duration-300",
        "border relative z-10",
        isFocus 
          ? "border-primary/40 bg-primary/10 shadow-[0_0_25px_-8px_hsl(32_95%_55%/0.5)]" 
          : "border-success/40 bg-success/10 shadow-[0_0_25px_-8px_hsl(142_70%_45%/0.5)]"
      )}
    >
      {/* Main clickable area - opens overlay */}
      <button
        onClick={handleMainClick}
        className={cn(
          "flex items-center gap-1.5 px-1 py-1 rounded-md transition-colors",
          "hover:bg-white/5"
        )}
      >
        {/* Animated flame icon with indicator dot */}
        <div className="relative animate-flame-flicker">
          <Flame className={cn(
            "w-4 h-4 transition-colors",
            isFocus ? "text-primary" : "text-success"
          )} />
          <span className={cn(
            "absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full",
            isFocus ? "bg-primary" : "bg-success"
          )} />
        </div>

        {/* Timer display */}
        <span className={cn(
          "font-display text-xs font-bold tabular-nums",
          isFocus ? "text-primary" : "text-success"
        )}>
          {timeLeft !== undefined ? formatTime(timeLeft) : '00:00'}
        </span>
      </button>

      {/* Separator */}
      <div className={cn(
        "w-px h-4",
        isFocus ? "bg-primary/20" : "bg-success/20"
      )} />

      {/* Inline controls */}
      <div className="flex items-center gap-0.5">
        {/* Music toggle - hidden until xl screens */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleRadio();
          }}
          className={cn(
            "hidden xl:flex items-center justify-center w-7 h-7 rounded-md transition-colors",
            isRadioPlaying 
              ? isFocus
                ? "text-primary bg-primary/20 hover:bg-primary/30" 
                : "text-success bg-success/20 hover:bg-success/30"
              : "text-muted-foreground/60 hover:text-foreground hover:bg-white/5"
          )}
          title={isRadioPlaying ? 'Pausar música' : 'Tocar música'}
        >
          <Music className={cn("w-3.5 h-3.5", isRadioPlaying && "animate-pulse")} />
        </button>

        {/* Play/Pause */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleTimer();
          }}
          className={cn(
            "flex items-center justify-center w-7 h-7 rounded-md transition-colors",
            isRunning 
              ? isFocus
                ? "text-primary bg-primary/20 hover:bg-primary/30" 
                : "text-success bg-success/20 hover:bg-success/30"
              : "text-muted-foreground/60 hover:text-foreground hover:bg-white/5"
          )}
          title={isRunning ? 'Pausar' : 'Continuar'}
        >
          {isRunning ? (
            <Pause className="w-3.5 h-3.5" />
          ) : (
            <Play className="w-3.5 h-3.5 ml-0.5" />
          )}
        </button>

        {/* Expand to overlay */}
        <button
          onClick={handleMainClick}
          className={cn(
            "flex items-center justify-center w-7 h-7 rounded-md transition-colors",
            "text-muted-foreground/60 hover:text-foreground hover:bg-white/5"
          )}
          title="Expandir"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
